import { zodResolver } from '@hookform/resolvers/zod';
import type { input } from 'zod';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { useEffect } from 'react';
import { UseFieldArrayUpdate, useFieldArray, useForm } from 'react-hook-form';
import { StyleObj } from '../../../../@types';
import { ConditionEntityType, ConditionProductType, ConditionTemplate, ConditionValue } from '../../../../@types/api';
import { CONDITIONS_COMBINATION_MESSAGE } from '../../../../constants';
import {
  ConditionFormData,
  ConditionTemplateFormData,
  conditionSchema,
  conditionTemplateSchema,
} from '../../../../schema';
import { useGetConditionGroups, useGetConditionTemplates, useGetConditionsById } from '../../../../queries/conditions';
import FormNumberInput from '../../../molecules/FormNumberInput';
import FormSelect from '../../../molecules/FormSelect';
import { mergeSx } from '../../../../utils/styles';

const styles: StyleObj = {
  container: {
    px: { xs: 1, md: 2 },
    py: { xs: 2, md: 3 },
  },
  formRow: (theme) => ({
    display: 'flex',
    gap: 1,
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  }),
  formFieldsWrapper: {
    display: 'flex',
    flex: 3,
    gap: 1,
  },
  textField: {},
  buttonsWrapper: {
    display: 'flex',
    gap: 1,
    justifyContent: 'flex-end',
    mt: 4,
  },
  conditionsCombinationLabel: {
    backgroundColor: 'background.lightGreen',
    border: '1px solid rgba(0, 83, 55, 0.20)',
    textAlign: 'center',
    p: {
      xs: 0.5,
      md: 1.25,
    },
    mb: {
      xs: 1,
      md: 1.5,
    },
  },
  tabs: {
    marginBottom: 2,
    '& .MuiButtonBase-root': {
      flex: 1,
    },
  },
};
type FormDataUnion = ConditionFormData | ConditionTemplateFormData;
type ConditionFormInput = input<typeof conditionSchema> | input<typeof conditionTemplateSchema>;

type ConditionsValuesFormProps<T extends FormDataUnion> = {
  onCancel: () => void;
  onSave: (values: T) => void;
  conditionGroupId: string | null;
  conditionModelType?: ConditionEntityType;
  modelId?: string | null;
  product?: ConditionProductType;
  preview?: boolean;
  formLabel?: string;
};

const defaultValues: FormDataUnion = {
  modelId: undefined,
  conditionGroupId: null,
  type: undefined,
  conditions: [],
};

const updateConditionValues = (
  conditions: ConditionValue[] | ConditionTemplate,
  update: UseFieldArrayUpdate<ConditionFormInput, 'conditions'>,
  conditionGroupId?: string | null
) => {
  if (conditions) {
    conditions.forEach((condition, index) => {
      update(index, {
        conditionTypeId: condition.conditionType?.id,
        restrictionValue: conditionGroupId === null ? null : condition.restrictionValue,
        verificationValue: conditionGroupId === null ? null : condition.verificationValue,
      });
    });
  }
};

const ConditionsValuesForm = <T extends FormDataUnion>({
  onCancel,
  onSave,
  conditionGroupId,
  conditionModelType,
  modelId,
  product,
  preview,
  formLabel,
}: ConditionsValuesFormProps<T>) => {
  const schema = modelId ? conditionSchema : conditionTemplateSchema;
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
  } = useForm<input<typeof schema>, unknown, FormDataUnion>({
    defaultValues,
    resolver: zodResolver(schema),
  });

  const { fields, update } = useFieldArray({
    control,
    name: 'conditions',
  });

  const conditionGroupFieldValue = watch('conditionGroupId');
  const conditionGroupIdValue = conditionGroupFieldValue || conditionGroupId;

  const { data: conditionsTemplateItems } = useGetConditionTemplates({
    conditionGroupId: conditionGroupIdValue,
    options: {
      enabled: !!conditionGroupFieldValue,
    },
  });
  const { data: conditions } = useGetConditionsById({ conditionModelType, modelId, product });
  const { data: conditionGroups } = useGetConditionGroups({ conditionModelType });

  const conditionsValue = conditionsTemplateItems && conditionGroupIdValue ? conditionsTemplateItems : conditions;

  const selectedConditionGroupDescription =
    conditionGroups?.find((group) => group.id === conditionGroupIdValue)?.description || '';

  // set default values when props are changed
  useEffect(() => {
    if (conditionModelType) {
      setValue('type', conditionModelType);
    }
    if (modelId) {
      setValue('modelId', modelId);
    }
    setValue('conditionGroupId', conditionGroupId);
  }, [setValue, update, conditionModelType, modelId, reset, conditionGroupId]);

  // update condition values based on a conditionGroupId, but only if conditionsTemplateItems are available
  useEffect(() => {
    if (conditionsTemplateItems) {
      setValue('conditions', []);
      updateConditionValues(conditionsTemplateItems, update);
    }
  }, [conditionsTemplateItems, update, setValue]);

  // if conditionGroupId is not set, update condition values based on conditions, if it is set, update based on conditionsTemplateItems
  useEffect(() => {
    if (conditions && !conditionGroupId) {
      updateConditionValues(conditions, update);
    }
  }, [conditions, update, conditionGroupId]);

  // update condition values when conditionGroupId is changed to null
  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      if (type !== 'change') return;

      if (conditions && name === 'conditionGroupId' && value.conditionGroupId === null) {
        updateConditionValues(conditions, update, value.conditionGroupId);
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, setValue, conditions, update, conditionGroupId]);

  const getFieldLabel = (conditionTypeId: string) => {
    return conditionsValue?.find((item) => {
      return item.conditionType.id === conditionTypeId;
    })?.conditionType.name;
  };

  const handleCancel = () => {
    onCancel();
  };

  const showConditionGroupSelect = conditionModelType !== 'ticket' && modelId && conditionGroups && !preview;

  const isBonusTicketCondition = product === 'BONUS';
  const hasConditionGroupSelected = !!modelId && !!conditionGroupFieldValue;
  const isRestrictionInputDisabled = hasConditionGroupSelected || preview;
  const isVerificationInputDisabled = hasConditionGroupSelected || preview;

  return (
    <Stack
      spacing={2}
      sx={mergeSx(styles.container, preview ? { backgroundColor: 'background.lighter', borderRadius: 2 } : {})}
    >
      {formLabel && (
        <Typography variant='subtitle1' align='center'>
          {formLabel}
        </Typography>
      )}

      <Stack spacing={1}>
        {showConditionGroupSelect && (
          <Box sx={styles.formRow}>
            <Typography variant='h5' sx={{ flex: '1 0 100px' }}>
              Conditions group
            </Typography>
            <FormSelect
              name='conditionGroupId'
              control={control}
              error={errors.conditionGroupId}
              options={conditionGroups}
              size='small'
              fullWidth
            />
          </Box>
        )}
        {fields.map((condition, index) => {
          const conditionLabel = getFieldLabel(condition.conditionTypeId);
          const isSameTicketsCondition = conditionLabel?.toLowerCase().includes('same tickets allowed');
          const isBonusExceptionEnabled = isBonusTicketCondition && isSameTicketsCondition && !preview;
          const isVerificationDisabled = isVerificationInputDisabled && !isBonusExceptionEnabled;

          return (
            <Stack key={condition.id}>
              <Box>
                {condition.conditionTypeId && (conditionLabel === 'Max Odds' || conditionLabel === 'Min Odds') && (
                  <Box sx={styles.conditionsCombinationLabel}>
                    <Typography variant='h5'>{CONDITIONS_COMBINATION_MESSAGE[conditionLabel]}</Typography>
                  </Box>
                )}
              </Box>
              {condition.conditionTypeId && (
                <Box sx={styles.formRow}>
                  <Typography variant='h5' sx={{ width: 100 }}>
                    {conditionLabel}
                  </Typography>
                  <Box sx={styles.formFieldsWrapper}>
                    <FormNumberInput
                      name={`conditions.${index}.restrictionValue` as const}
                      control={control}
                      error={errors.conditions?.[index]?.restrictionValue}
                      sx={styles.textField}
                      size='small'
                      allowDecimals={!isSameTicketsCondition}
                      valueAsString
                      disabled={isRestrictionInputDisabled}
                      fullWidth
                      slotProps={{
                        input: {
                          endAdornment: '(R)',
                        },
                      }}
                    />
                    <FormNumberInput
                      name={`conditions.${index}.verificationValue` as const}
                      control={control}
                      error={errors.conditions?.[index]?.verificationValue}
                      sx={styles.textField}
                      size='small'
                      allowDecimals={!isSameTicketsCondition}
                      valueAsString
                      disabled={isVerificationDisabled}
                      fullWidth
                      slotProps={{
                        input: {
                          endAdornment: '(V)',
                        },
                      }}
                    />
                  </Box>
                </Box>
              )}
            </Stack>
          );
        })}
        {selectedConditionGroupDescription && (
          <Box sx={styles.formRow}>
            <Typography variant='h5' sx={{ flex: '1 0 100px' }}>
              Description
            </Typography>
            <TextField
              multiline
              id='description'
              type='text'
              fullWidth
              value={selectedConditionGroupDescription}
              disabled
            />
          </Box>
        )}
      </Stack>

      {!preview && (
        <Box sx={styles.buttonsWrapper}>
          <Button variant='outlined' onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant='contained' onClick={handleSubmit((data) => onSave(data as T))}>
            Save
          </Button>
        </Box>
      )}
    </Stack>
  );
};

export default ConditionsValuesForm;
